import {
  RampIdType,
  useOrganizationRolePermissionsQuery,
} from '../../../generated/graphql';
import {type ApolloError} from '../../../state/graphql/apolloWithAbort';
import {useOrgRampFlagWithLoading} from '../../../util/useRampFlag';

export enum DefaultRoleType {
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer',
}

type Role = {
  id: string;
  permissions: Permission[];
};

export type Permission = {
  name: string;
  displayName: string;
  groupName: string;
};

export type DefaultRole = Role & {
  roleType: DefaultRoleType;
};

export type CustomRole = Role & {
  name: string;
  description: string | null;
  inheritedFrom: DefaultRoleType;
};

export type OrganizationRolesResult = {
  loading: boolean;
  error?: ApolloError;
  refetch: () => {};
  data: {
    defaultRoles: DefaultRole[];
    customRoles: CustomRole[];
    orgName: string;
    orgId?: string;
  };
};

const SANDBOX_CREATE_PERMISSION = 'sandbox:create';

export function useOrgRolesContextResult(orgName: string) {
  const {loading, refetch, data, error} = useOrganizationRolePermissionsQuery({
    variables: {
      orgName,
    },
    fetchPolicy: 'cache-and-network',
    skip: orgName === '',
  });

  const roles = data?.organization?.roles ?? [];
  const orgId = data?.organization?.id;
  const {data: canUseSandboxFeature, loading: canUseSandboxFeatureLoading} =
    useOrgRampFlagWithLoading('sandboxes', RampIdType.OrgId, orgId);
  const showSandboxCreatePermission =
    orgId != null && canUseSandboxFeature === true;

  const defaultRoles = roles
    .filter(role => !role.inheritedFrom)
    .map(role => ({
      id: role.ID,
      roleType: role.name as DefaultRoleType,
      permissions: role.permissions.filter(
        p => p.name !== SANDBOX_CREATE_PERMISSION || showSandboxCreatePermission
      ),
    }));

  const customRoles = roles
    .filter(role => role.inheritedFrom)
    .map(role => ({
      id: role.ID,
      name: role.name,
      inheritedFrom: role.inheritedFrom?.name as DefaultRoleType,
      description: role.description ?? null,
      permissions: role.permissions.filter(
        p => p.name !== SANDBOX_CREATE_PERMISSION || showSandboxCreatePermission
      ),
    }));

  return {
    loading: loading || (orgId != null && canUseSandboxFeatureLoading),
    error,
    refetch,
    data: {
      defaultRoles,
      customRoles,
      orgName,
      orgId,
    },
  };
}
